import { SearchInput } from "@src/components/ui-components/Inputs/SearchInput/SearchInput";
import { Fragment, useEffect, useState } from "react";
import Popup from "reactjs-popup";
import { EventType, PopupPosition } from "reactjs-popup/dist/types";
import { LoadingIndicator } from "../../ui-components/LoadingIndicator/LoadingIndicator";
import "./ActionsPopover.css";
import styles from "./ActionsPopover.module.css";

export interface ActionsPopoverAction {
  title: string;
  iconName?: string;
  trailingIconName?: string;
  iconColor?: string;
  textColor?: string;
  onClick?: () => void;
  link?: string;
  popoverMenu?: (trigger: JSX.Element) => JSX.Element;
  dataCy?: string;
}
export interface ActionsPopoverActionGroup {
  actions: ActionsPopoverAction[];
  /** Optional title if group is expandable */
  expandableTitle?: string;
}
interface ActionsPopoverProps {
  actions?: ActionsPopoverAction[] | Promise<ActionsPopoverAction[]>;
  actionGroups?: ActionsPopoverActionGroup[];
  headerView?: JSX.Element | JSX.Element[];
  trigger: JSX.Element | ((isOpen: boolean) => JSX.Element);
  position?: PopupPosition | PopupPosition[];
  subPosition?: PopupPosition | PopupPosition[];
  on?: EventType | EventType[];
  includeActionSearch?: boolean;
  className?: string;
  maxWidth?: number | string;
  minWidth?: number | string;
  open?: boolean;
  onChangeOpen?: (open: boolean) => void;
  forceCloseChildren?: boolean;
}

export const ActionsPopover = (props: ActionsPopoverProps) => {
  const [actions, setActions] = useState<ActionsPopoverAction[]>([]);
  const [searchText, setSearchText] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [shouldLoadActions, setShouldLoadActions] = useState(false);

  useEffect(() => {
    setShouldLoadActions(false);
    if (props.actions) {
      if (Array.isArray(props.actions)) {
        setActions(props.actions);
      } else {
        setShouldLoadActions(true);
      }
    }
  }, [props.actions]);

  function getJSXForAction(action: ActionsPopoverAction, index: number) {
    const trailingIcon = action.trailingIconName ?? (action.link ? "open_in_new" : action.popoverMenu ? "arrow_right" : undefined);
    const jsx = (
      <li
        key={index}
        onClick={() => {
          if (action.onClick) action.onClick();
        }}
        data-cy={action.dataCy}
        style={{ color: action.textColor ? action.textColor : undefined }}
      >
        {action.iconName && (
          <span className="material-icons-outlined" style={action.iconColor ? { color: action.iconColor } : {}}>
            {action.iconName}
          </span>
        )}
        {action.title}
        {trailingIcon && (
          <span className={"material-icons-outlined " + styles.trailingIcon} style={action.iconColor ? { color: action.iconColor } : {}}>
            {trailingIcon}
          </span>
        )}
      </li>
    );
    return action.link ? (
      <a href={action.link} key={action.link}>
        {jsx}
      </a>
    ) : (
      jsx
    );
  }

  let className = styles.container;
  if (props.className) className += ` ${props.className}`;

  let filteredActionGroups: ActionsPopoverActionGroup[] = [];
  if (actions.length > 0) {
    filteredActionGroups.push({
      actions: actions.filter((action) => !searchText || action.title.toLowerCase().includes(searchText.toLowerCase())),
    });
  }

  for (const group of props.actionGroups ?? []) {
    const newGroup =
      searchText.length > 0 ? group.actions.filter((action) => action.title.toLowerCase().includes(searchText.toLowerCase())) : group.actions;
    if (newGroup.length > 0) filteredActionGroups.push({ actions: newGroup, expandableTitle: group.expandableTitle });
  }

  const loadActions = async () => {
    if (props.actions && !Array.isArray(props.actions)) {
      setLoadingActions(true);
      const actions = await props.actions;
      setActions(actions);
      setLoadingActions(false);
      setShouldLoadActions(false);
    }
  };

  return (
    <Popup
      on={props.on ? props.on : ["focus", "hover"]}
      position={props.position ? props.position : "bottom center"}
      trigger={typeof props.trigger === "function" ? props.trigger(loadingActions) : <span>{props.trigger}</span>}
      arrow
      keepTooltipInside
      arrowStyle={{ zIndex: 14 }}
      overlayStyle={{ zIndex: 14 }}
      mouseLeaveDelay={100}
      closeOnEscape
      contentStyle={{ zIndex: 9999 }}
      nested
      onOpen={() => {
        if (!loadingActions && props.actions && shouldLoadActions) {
          loadActions();
        }
        props.onChangeOpen?.(true);
      }}
      onClose={() => {
        props.onChangeOpen?.(false);
      }}
      open={props.open}
    >
      <div style={{ maxWidth: props.maxWidth ?? "60vw", minWidth: props.minWidth }} className={className}>
        {props.headerView && <div className={styles.headerView}>{props.headerView}</div>}
        {props.includeActionSearch && (
          <div className={styles.searchBox}>
            <SearchInput value={searchText} onChange={(text) => setSearchText(text)} />
          </div>
        )}
        {loadingActions && <LoadingIndicator centerHorizontally />}

        <div className={`${styles.actionsList} ${props.includeActionSearch ? styles.searchEnabled : ""}`} data-cy="actions-popover-list">
          {filteredActionGroups.map((group, index) => {
            const isExpanded = expandedGroups.includes(group.expandableTitle ?? "") || searchText.length > 0;
            return (
              <div key={"action-group-" + index}>
                <ul>
                  {group.expandableTitle && (
                    <li
                      key={`expandable-${index}`}
                      onClick={() => {
                        if (group.expandableTitle) {
                          if (isExpanded) {
                            setExpandedGroups(expandedGroups.filter((title) => title !== group.expandableTitle));
                          } else {
                            setExpandedGroups([...expandedGroups, group.expandableTitle]);
                          }
                        }
                      }}
                    >
                      {group.expandableTitle}
                      <span className={`material-icons-outlined ${styles.trailingIcon}`}>{isExpanded ? "expand_more" : "chevron_right"}</span>
                    </li>
                  )}

                  {(!group.expandableTitle || isExpanded) &&
                    group.actions.map((action, index) => {
                      return action.popoverMenu ? (
                        <Fragment key={action.title}>{action.popoverMenu(getJSXForAction(action, index))}</Fragment>
                      ) : (
                        <Fragment key={action.title}>{getJSXForAction(action, index)}</Fragment>
                      );
                    })}
                </ul>
                {index < filteredActionGroups.length - 1 && <hr />}
              </div>
            );
          })}
        </div>
      </div>
    </Popup>
  );
};
